import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

// Wedding day: 19 May 2027 in Cyprus (EEST, UTC+3).
// We build a lookup keyed by minutes-since-midnight-Cyprus, so a visitor in any
// timezone experiences the ribbon at the "right" Cyprus moment.
const CYPRUS_UTC_OFFSET_MIN = 180; // EEST = UTC+3
const WEDDING_YEAR = 2027;
const WEDDING_MONTH = 5; // May (1-indexed for our helpers)
const WEDDING_DAY = 19;

/** Return Cyprus-local {year, month, day, minutesSinceMidnight} for `now`. */
const cyprusNow = (now = new Date()) => {
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
  const cyprusMs = utcMs + CYPRUS_UTC_OFFSET_MIN * 60_000;
  const d = new Date(cyprusMs);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    minutes: d.getUTCHours() * 60 + d.getUTCMinutes(),
  };
};

const CEREMONY_MIN = 15 * 60 + 30; // 15:30

/** Choose the ribbon's active phase for the given Cyprus-minutes. */
const phaseFor = (minutes) => {
  if (minutes < CEREMONY_MIN - 30) return "beforeDay";      // 00:00–15:00 → "in X h Y m"
  if (minutes < CEREMONY_MIN) return "imminent";            // 15:00–15:30 → "very soon"
  if (minutes < CEREMONY_MIN + 60) return "ceremony";       // 15:30–16:30 → "now"
  if (minutes < 20 * 60) return "reception";                // 16:30–20:00 → souvlaki + music
  return "party";                                            // 20:00+     → last dances
};

const humanCountdown = (fromMin, toMin) => {
  const diff = Math.max(0, toMin - fromMin);
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (h >= 1) return `${h}h ${m}m`;
  return `${m}m`;
};

const LiveDayRibbon = () => {
  const { t } = useLang();
  const [msg, setMsg] = useState(null); // string | null

  useEffect(() => {
    const compute = () => {
      const c = cyprusNow();
      // Only visible during the entirety of May 19, 2027 in Cyprus.
      if (c.year !== WEDDING_YEAR || c.month !== WEDDING_MONTH || c.day !== WEDDING_DAY) {
        setMsg(null);
        return;
      }
      const phase = phaseFor(c.minutes);
      let text = "";
      switch (phase) {
        case "beforeDay": {
          const cd = humanCountdown(c.minutes, CEREMONY_MIN);
          text = `${t("ribbon.ceremonyIn") || "Ceremony in"} ${cd} · 15:30 · ${t("ribbon.beach") || "Ammos Kalambouri Beach"}`;
          break;
        }
        case "imminent":
          text = t("ribbon.veryClose") || "Grab a spot on the sand. Ceremony very soon.";
          break;
        case "ceremony":
          text = t("ribbon.nowCeremony") || "Now: the ceremony. The sea does the rest.";
          break;
        case "reception":
          text = t("ribbon.reception") || "Souvlaki. Music. The sea. Toast with us.";
          break;
        case "party":
        default:
          text = t("ribbon.party") || "One last dance — the night decides.";
      }
      setMsg(text);
    };
    compute();
    const iv = setInterval(compute, 30_000);
    return () => clearInterval(iv);
  }, [t]);

  if (!msg) return null;

  return (
    <div
      className="w-full bg-foreground text-background text-center py-2 px-4 relative z-30"
      data-testid="live-day-ribbon"
    >
      <p className="inline-flex items-center gap-2 text-[11px] sm:text-xs tracking-[0.22em] uppercase">
        <Sparkles className="w-3 h-3 text-accent" />
        <span className="truncate max-w-[90vw]">{msg}</span>
        <Sparkles className="w-3 h-3 text-accent" />
      </p>
    </div>
  );
};

export default LiveDayRibbon;
